import type {
  AnalyseTexte,
  Finalite,
  Invariant,
  ProfilTexte,
  VerrouInvariant,
} from '@rosetta/shared';
import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { ServiceIndisponibleError, SessionExpireeError } from '../erreurs';
import { type CorrespondanceTypes, type LigneSousTache, lireSousTaches } from './etapes';
import { CODE_LANGUE, idSchema, lireLignes, texteLocaliseSchema } from './h2data';
import type { AppelHttp } from './http';
import { TABLE_TACHE } from './taches';

/**
 * What the analysis of a finished translation tells about its text (#9, #10): the purpose of
 * the text, its profile (who speaks and to whom, its kind, its tone, its objective) and the
 * invariants the translation must keep. Tahiti computes them while it runs the
 * `rose_task_v5_multi_translation` task, as sub-tasks of it:
 *
 *   - the purpose and the profile are in the `result_json` of the `rose_task_v5_analyse`
 *     sub-task (`output_json.finalite` and `finalite_explication`; `voix`, `genre`, `ton` and
 *     `objectif.formulation`, #10);
 *   - the invariants are in the `result_json` of the `rose_task_v4_detect_invariants`
 *     sub-task (`values`), and, as a fallback, in the rows of `rose_invariant` of that sub-task;
 *   - the sub-tasks are found in the `result_json` of the main task (`first_stage_tasks`: a
 *     `task_id`, a `task_table` and an `algorithm_code` each) or, when that is null or does not
 *     list them, by `h2_task_main_id` and the `physical_name` of their task type, as the waiting
 *     screen and the interruption do (#7, `etapes.ts`).
 *
 * A `result_json` is a JSON string, parsed here (an already decoded object is accepted). The
 * label of an invariant type is the `title` of `rose_invariant_type` ("Nom", "Dénomination"),
 * read once per analysis for both paths (`type_code` of `values`, `type_id` of the rows); without
 * a title the code stands, its underscores turned into spaces. A lock is "strict" for
 * `preserve_exact` (reproduced as it is) and "souple" for `translate_controlled` and
 * `format_controlled` (the element may be adapted: only `preserve_exact` is kept exactly); a lock
 * Tahiti has not told us about leaves the invariant out.
 *
 * Rosetta writes none of it: the screens show what Tahiti said. Everything is read, nothing is
 * written. The tables read are the ones named here, never one named by the data.
 *
 * Each part is read on its own and leniently: a sub-task that is missing, that failed (its
 * `result_json` then holds an error) or whose answer is not what is expected gives `null` for
 * that part (the screen then hides it), it is not an error. A call that fails (network, session)
 * still raises.
 *
 * Seen on devdb on 2026-10-07, read only (sub-tasks 910305, invariants, and 910342, analysis):
 * the shape of `values` (`id`, `value`, `type_code`, `placeholder`, `lock_mode_code`; no label of
 * type), the columns of `rose_invariant`, the dictionaries `rose_invariant_type` (`id`, `code`,
 * `title`) and `rose_invariant_lock_mode` (`id`, `code`, `title`; three modes), the `result_json`
 * of the purpose, the `result_json` of a failed step (an error), and a main task whose
 * `result_json` is sometimes null (910633).
 *
 * TO VALIDATE(#9): none of this was played with the account of an end user (that it may read the
 * dictionaries, `rose_invariant` and the sub-tasks), and the fallback on the rows of
 * `rose_invariant` was not needed in what was seen (`values` was complete).
 */

export const TABLE_ANALYSE = 'rose_task_v5_analyse';
export const TABLE_INVARIANTS = 'rose_task_v4_detect_invariants';
const TABLE_INVARIANT = 'rose_invariant';
const TABLE_TYPE_INVARIANT = 'rose_invariant_type';
const TABLE_VERROU_INVARIANT = 'rose_invariant_lock_mode';

const ALGORITHME_ANALYSE = 'rosetta_v5_analyse';
const ALGORITHME_INVARIANTS = 'rosetta_v5_invariants';

/**
 * The lock codes of Tahiti (`rose_invariant_lock_mode.code`) and what they mean here: only
 * `preserve_exact` is reproduced as it is, the two others let the translation adapt the element.
 */
const VERROUS: Readonly<Record<string, VerrouInvariant>> = {
  preserve_exact: 'strict',
  translate_controlled: 'souple',
  format_controlled: 'souple',
};

/** A task row, as far as this file reads it: `result_json` is a string, or already an object. */
const ligneResultatSchema = z.object({ id: idSchema, result_json: z.unknown().optional() });

/**
 * A JSON column: Tahiti writes it as a string. An object (a column H2Data already decoded)
 * is accepted as it is; a string that is not JSON gives `undefined`, the part is then
 * unreadable.
 */
export function lireJson(valeur: unknown): unknown {
  if (typeof valeur !== 'string') return valeur;
  try {
    return JSON.parse(valeur) as unknown;
  } catch {
    return undefined;
  }
}

/** The `result_json` of a row of `table`, parsed; `undefined` when there is no row or no JSON. */
export async function lireResultat(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  table: string,
  id: string,
): Promise<unknown> {
  const [ligne] = await lireLignes(
    appel,
    config,
    jeton,
    {
      table,
      fields: ['id', 'result_json'],
      where: ['id', Number(id), '='],
      limit: 1,
      lang: CODE_LANGUE,
    },
    ligneResultatSchema,
  );
  return lireJson(ligne?.result_json);
}

/* ── Finding the two sub-tasks ───────────────────────────────────────────────────── */

const etapesSchema = z.object({
  first_stage_tasks: z.array(
    z
      .object({
        task_id: idSchema,
        task_table: z.string().nullish(),
        algorithm_code: z.string().nullish(),
      })
      .passthrough(),
  ),
});

/** Identifiers of the sub-tasks of the two steps; `null` for one that could not be found. */
interface Etapes {
  analyse: string | null;
  invariants: string | null;
}

/** The sub-tasks of the two steps, from the `first_stage_tasks` of the main task, if it lists them. */
function etapesDuResultat(resultat: unknown): Etapes {
  const lues = etapesSchema.safeParse(resultat);
  const liste = lues.success ? lues.data.first_stage_tasks : [];
  const trouver = (algorithme: string, table: string) =>
    liste.find((e) => e.algorithm_code === algorithme || e.task_table === table)?.task_id ?? null;
  return {
    analyse: trouver(ALGORITHME_ANALYSE, TABLE_ANALYSE),
    invariants: trouver(ALGORITHME_INVARIANTS, TABLE_INVARIANTS),
  };
}

/**
 * The sub-tasks of the two steps, found through `h2_task_main_id` and the `physical_name` of
 * their task type: the most recent one of each type (the highest identifier: a step that was
 * rerun).
 */
async function etapesParSousTaches(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  tacheId: string,
  types: () => Promise<CorrespondanceTypes>,
): Promise<Etapes> {
  const [sousTaches, correspondance] = await Promise.all([
    lireSousTaches(appel, config, jeton, tacheId),
    types(),
  ]);
  return {
    analyse: derniereDuType(sousTaches, correspondance, TABLE_ANALYSE),
    invariants: derniereDuType(sousTaches, correspondance, TABLE_INVARIANTS),
  };
}

/**
 * The most recent sub-task (the highest identifier: a step that was rerun) whose task type has
 * this `physical_name`, or `null` when there is none. Shared with the control (#10).
 */
export function derniereDuType(
  sousTaches: readonly LigneSousTache[],
  correspondance: CorrespondanceTypes,
  nom: string,
): string | null {
  const ids = sousTaches
    .filter(
      (ligne) => ligne.h2_tasktype_id != null && correspondance.get(ligne.h2_tasktype_id) === nom,
    )
    .map((ligne) => ligne.id)
    .sort((a, b) => Number(b) - Number(a));
  return ids[0] ?? null;
}

/* ── The purpose ─────────────────────────────────────────────────────────────────── */

const finaliteBruteSchema = z.object({
  success: z.boolean().optional(),
  output_json: z
    .object({
      finalite: z.string().trim().min(1),
      finalite_explication: z.string().trim().min(1).nullish(),
    })
    .passthrough(),
});

function lireFinalite(resultat: unknown): Finalite | null {
  const lu = finaliteBruteSchema.safeParse(resultat);
  if (!lu.success || lu.data.success === false) return null;
  const { finalite, finalite_explication } = lu.data.output_json;
  return {
    libelle: finalite,
    ...(finalite_explication == null ? {} : { explication: finalite_explication }),
  };
}

/* ── The profile (#10) ───────────────────────────────────────────────────────────── */

/** A text field of the analysis: a non-empty text, anything else is "not given". */
const champTexte = z.preprocess(
  (valeur) => (typeof valeur === 'string' && valeur.trim() !== '' ? valeur.trim() : undefined),
  z.string().optional(),
);

const profilBrutSchema = z.object({
  success: z.boolean().optional(),
  output_json: z
    .object({
      voix: champTexte,
      genre: champTexte,
      ton: champTexte,
      objectif: z.preprocess(
        (valeur) =>
          typeof valeur === 'object' && valeur !== null
            ? (valeur as Record<string, unknown>).formulation
            : undefined,
        champTexte,
      ),
    })
    .passthrough(),
});

/**
 * Who speaks and to whom, the kind of text, its tone and its objective, word for word. Each
 * field stands on its own (one that is missing or not a text is left out); `null` when the
 * answer is not one of the step, or gives none of the four.
 */
function lireProfil(resultat: unknown): ProfilTexte | null {
  const lu = profilBrutSchema.safeParse(resultat);
  if (!lu.success || lu.data.success === false) return null;
  const { voix, genre, ton, objectif } = lu.data.output_json;
  const profil: ProfilTexte = {
    ...(voix === undefined ? {} : { voix }),
    ...(genre === undefined ? {} : { genre }),
    ...(ton === undefined ? {} : { ton }),
    ...(objectif === undefined ? {} : { objectif }),
  };
  return Object.keys(profil).length === 0 ? null : profil;
}

/* ── The invariants ──────────────────────────────────────────────────────────────── */

const ligneCodeSchema = z.object({ id: idSchema, code: z.string().nullish() });
const ligneTypeInvariantSchema = z.object({
  id: idSchema,
  code: z.string().nullish(),
  title: texteLocaliseSchema.nullish(),
});

/** The labels of the invariant types, by code (`values`) and by identifier (the rows). */
interface TypesInvariants {
  parCode: ReadonlyMap<string, string>;
  parId: ReadonlyMap<string, string>;
}

const SANS_TYPES: TypesInvariants = { parCode: new Map(), parId: new Map() };

/**
 * Reads the dictionary of the invariant types once (`rose_invariant_type`: `id`, `code`,
 * `title`). Without it the codes stand, their underscores turned into spaces: a dictionary that
 * cannot be read costs the labels, not the analysis (a refused session still raises).
 */
async function lireTypesInvariants(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
): Promise<TypesInvariants> {
  try {
    const lignes = await lireLignes(
      appel,
      config,
      jeton,
      {
        table: TABLE_TYPE_INVARIANT,
        fields: ['id', 'code', 'title'],
        limit: 200,
        lang: CODE_LANGUE,
      },
      ligneTypeInvariantSchema,
    );
    const parCode = new Map<string, string>();
    const parId = new Map<string, string>();
    for (const ligne of lignes) {
      const titre = ligne.title?.trim();
      if (titre == null || titre === '') continue;
      parId.set(ligne.id, titre);
      if (ligne.code != null) parCode.set(ligne.code, titre);
    }
    return { parCode, parId };
  } catch (erreur) {
    if (erreur instanceof SessionExpireeError) throw erreur;
    // Explicit fallback, not an error: the labels of the types are then their codes.
    if (erreur instanceof ServiceIndisponibleError) return SANS_TYPES;
    throw erreur;
  }
}

/** "legal_reference" -> "legal reference": the label of a type that has no title. */
const libelleDuCode = (code: string): string => code.replace(/_/g, ' ');

/** The invariant of a value, a type label and a lock code; `null` when the lock is unknown. */
function invariantDe(
  valeur: string,
  type: string | null | undefined,
  codeVerrou: string | null | undefined,
): Invariant | null {
  const verrou = codeVerrou == null ? undefined : VERROUS[codeVerrou];
  if (verrou === undefined) return null;
  return { valeur, type: type?.trim() || 'autre', verrou };
}

/** The same value under the same lock written twice is one invariant. */
function sansDoublon(invariants: Invariant[]): Invariant[] {
  const vus = new Set<string>();
  return invariants.filter((invariant) => {
    const cle = `${invariant.verrou}|${invariant.valeur}`;
    if (vus.has(cle)) return false;
    vus.add(cle);
    return true;
  });
}

const elementValeurSchema = z
  .object({
    value: z.string().min(1),
    type_code: z.string().nullish(),
    /** Not in what Tahiti writes (seen 2026-10-07): tolerated if a version gives one. */
    type_libelle: z.string().nullish(),
    lock_mode_code: z.string().nullish(),
  })
  .passthrough();

const invariantsBrutsSchema = z
  .object({
    success: z.boolean().optional(),
    values: z.array(z.unknown()).optional(),
    invariant_count: z.number().optional(),
    invariant_ids: z.array(z.unknown()).optional(),
  })
  .passthrough();

/**
 * The invariants of the `result_json` of their step: `null` when it is not an answer of the
 * step (a step that failed holds an error, no `values` and no count), a list otherwise;
 * `'repli'` when the answer says there are invariants but not in a form that can be read (the
 * rows of `rose_invariant` are then read).
 */
async function lireValeurs(
  resultat: unknown,
  types: () => Promise<TypesInvariants>,
): Promise<Invariant[] | null | 'repli'> {
  const lu = invariantsBrutsSchema.safeParse(resultat);
  if (!lu.success || lu.data.success === false) return null;
  const { values, invariant_count, invariant_ids } = lu.data;
  if (values === undefined && invariant_count === undefined && invariant_ids === undefined) {
    return null;
  }
  const annonces = invariant_count ?? invariant_ids?.length ?? 0;
  if (values === undefined) return annonces > 0 ? 'repli' : [];

  const elements = values.map((brut) => elementValeurSchema.safeParse(brut));
  if (elements.some((element) => !element.success)) return 'repli';
  if (elements.length === 0) return annonces > 0 ? 'repli' : [];

  const dictionnaire = await types();
  return sansDoublon(
    elements.flatMap((element) => {
      if (!element.success) return [];
      const { value, type_code, type_libelle, lock_mode_code } = element.data;
      const libelle =
        (type_code == null ? undefined : dictionnaire.parCode.get(type_code)) ??
        type_libelle ??
        (type_code == null ? null : libelleDuCode(type_code));
      const invariant = invariantDe(value, libelle, lock_mode_code);
      return invariant === null ? [] : [invariant];
    }),
  );
}

const ligneInvariantSchema = z.object({
  id: idSchema,
  original_value: texteLocaliseSchema.nullish(),
  type_id: idSchema.nullish(),
  lock_mode_id: idSchema.nullish(),
});

/** The invariants of a sub-task, from the table `rose_invariant` (fallback of `values`). */
async function lireLignesInvariants(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  tacheId: string,
  types: () => Promise<TypesInvariants>,
): Promise<Invariant[]> {
  const lignes = await lireLignes(
    appel,
    config,
    jeton,
    {
      table: TABLE_INVARIANT,
      fields: ['id', 'original_value', 'type_id', 'lock_mode_id'],
      where: ['h2_task_id', Number(tacheId), '='],
      limit: 500,
      lang: CODE_LANGUE,
    },
    ligneInvariantSchema,
  );
  if (lignes.length === 0) return [];

  const [dictionnaire, verrous] = await Promise.all([
    types(),
    lireLignes(
      appel,
      config,
      jeton,
      { table: TABLE_VERROU_INVARIANT, fields: ['id', 'code'], limit: 50 },
      ligneCodeSchema,
    ),
  ]);
  const codeVerrou = (id: string | null | undefined) =>
    verrous.find((ligne) => ligne.id === id)?.code;

  return sansDoublon(
    lignes.flatMap((ligne) => {
      const valeur = ligne.original_value?.trim();
      if (valeur == null || valeur === '') return [];
      const invariant = invariantDe(
        valeur,
        ligne.type_id == null ? null : dictionnaire.parId.get(ligne.type_id),
        codeVerrou(ligne.lock_mode_id),
      );
      return invariant === null ? [] : [invariant];
    }),
  );
}

/* ── The analysis ────────────────────────────────────────────────────────────────── */

/**
 * Reads the analysis of a finished translation. Both steps are read in parallel; a part that
 * Tahiti did not give, or gave in a form that cannot be read, is `null`. `types` gives the names
 * of the Rosetta task types (kept by the caller for the session), read only when the
 * sub-tasks have to be found through `h2_task_main_id`.
 */
export async function lireAnalyseTexte(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  tacheId: string,
  types: () => Promise<CorrespondanceTypes>,
): Promise<AnalyseTexte> {
  let etapes = etapesDuResultat(await lireResultat(appel, config, jeton, TABLE_TACHE, tacheId));
  // The main task did not list a step (its `result_json` is null, or older): the sub-tasks say.
  if (etapes.analyse === null || etapes.invariants === null) {
    const trouvees = await etapesParSousTaches(appel, config, jeton, tacheId, types);
    etapes = {
      analyse: etapes.analyse ?? trouvees.analyse,
      invariants: etapes.invariants ?? trouvees.invariants,
    };
  }

  // One read of the dictionary of the types, shared by both paths of the invariants.
  let dictionnaire: Promise<TypesInvariants> | null = null;
  const typesInvariants = () => {
    dictionnaire ??= lireTypesInvariants(appel, config, jeton);
    return dictionnaire;
  };

  const idInvariants = etapes.invariants;
  const [analyse, invariants] = await Promise.all([
    etapes.analyse === null
      ? null
      : lireResultat(appel, config, jeton, TABLE_ANALYSE, etapes.analyse).then((resultat) => ({
          finalite: lireFinalite(resultat),
          profil: lireProfil(resultat),
        })),
    idInvariants === null
      ? null
      : lireResultat(appel, config, jeton, TABLE_INVARIANTS, idInvariants).then(
          async (resultat) => {
            const valeurs = await lireValeurs(resultat, typesInvariants);
            return valeurs === 'repli'
              ? lireLignesInvariants(appel, config, jeton, idInvariants, typesInvariants)
              : valeurs;
          },
        ),
  ]);
  return { finalite: analyse?.finalite ?? null, profil: analyse?.profil ?? null, invariants };
}
